import { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Segmented, TextField } from '@scripsteam/scrips-signal-ds';
import type { OrgLocation } from '@/auth/types';

/**
 * Doctor Share editor — compact dialog matching the Flutter UI.
 *
 * PM parity: `dev-scrips-pm-react/src/billing/components/doctor-share-dialog.tsx`.
 * Recomposed on Signal DS atoms: PM's shadcn `Dialog`/`DialogContent` →
 * DS `Modal`; PM's hand-rolled radio buttons → DS `Segmented`; PM's bare
 * `<input>` cells → DS `TextField` (compact). No hardcoded hex.
 *
 * Each location renders its own section: title in uppercase, Percent /
 * Fixed segmented toggle, three inline inputs (Cash / Insurance / Corporate)
 * for percent mode or a single Amount input for fixed mode. `Done` is
 * disabled until every touched location is valid (percents sum to 100 or
 * fixed > 0) — mirrors Flutter, which has no running-sum indicator.
 */

export interface ShareInput {
  isPercent: boolean;
  cash: string;
  insurance: string;
  corporate: string;
  fixed: string;
}

interface DoctorShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  locations: OrgLocation[];
  /** Provider fees draft from the parent — only locations with a fee are editable. */
  providerFees: Record<string, { fee: string }>;
  /** Currently-configured shares, keyed by location.id. */
  initialShares: Record<string, ShareInput>;
  onApply: (shares: Record<string, ShareInput>) => void;
}

function blankShare(): ShareInput {
  return { isPercent: true, cash: '0', insurance: '0', corporate: '0', fixed: '0' };
}

export function DoctorShareDialog({
  open,
  onOpenChange,
  locations,
  providerFees,
  initialShares,
  onApply,
}: DoctorShareDialogProps) {
  const eligibleLocations = useMemo(
    () =>
      locations.filter((loc) => {
        if (!loc.id) return false;
        const v = providerFees[loc.id]?.fee?.trim();
        return Boolean(v) && parseFloat(v ?? '') > 0;
      }),
    [locations, providerFees],
  );

  const [draft, setDraft] = useState<Record<string, ShareInput>>({});

  useEffect(() => {
    if (!open) return;
    const next: Record<string, ShareInput> = {};
    for (const loc of eligibleLocations) {
      if (!loc.id) continue;
      next[loc.id] = initialShares[loc.id] ?? blankShare();
    }
    setDraft(next);
  }, [open, eligibleLocations, initialShares]);

  const allValid = useMemo(() => {
    if (Object.keys(draft).length === 0) return false;
    for (const id of Object.keys(draft)) {
      const s = draft[id];
      if (s.isPercent) {
        const sum =
          (parseFloat(s.cash) || 0) +
          (parseFloat(s.insurance) || 0) +
          (parseFloat(s.corporate) || 0);
        if (Math.abs(sum - 100) >= 0.01) return false;
      } else {
        const v = parseFloat(s.fixed);
        if (Number.isNaN(v) || v <= 0) return false;
      }
    }
    return true;
  }, [draft]);

  const update = (locId: string, patch: Partial<ShareInput>) => {
    setDraft((prev) => ({
      ...prev,
      [locId]: { ...(prev[locId] ?? blankShare()), ...patch },
    }));
  };

  const onDone = () => {
    if (!allValid) return;
    onApply(draft);
  };

  return (
    <Modal
      open={open}
      onClose={() => onOpenChange(false)}
      title="Doctor Share"
      size="md"
      footer={
        eligibleLocations.length === 0 ? (
          <Button variant="tertiary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        ) : (
          <>
            <Button variant="tertiary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={onDone} disabled={!allValid}>
              Done
            </Button>
          </>
        )
      }
    >
      {eligibleLocations.length === 0 ? (
        <p className="text-sm text-text-muted">
          Set a provider fee for at least one location before configuring shares.
        </p>
      ) : (
        <div className="flex flex-col gap-6">
          {eligibleLocations.map((loc) => {
            if (!loc.id) return null;
            const share = draft[loc.id] ?? blankShare();
            return (
              <ShareSection
                key={loc.id}
                locationName={loc.name ?? 'Location'}
                share={share}
                onChange={(patch) => update(loc.id!, patch)}
              />
            );
          })}
        </div>
      )}
    </Modal>
  );
}

// ─── Per-location section ────────────────────────────────────────────────

interface ShareSectionProps {
  locationName: string;
  share: ShareInput;
  onChange: (patch: Partial<ShareInput>) => void;
}

const MODE_OPTIONS = [
  { label: 'Percent', value: 'percent' as const },
  { label: 'Fixed', value: 'fixed' as const },
];

function ShareSection({ locationName, share, onChange }: ShareSectionProps) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-4">
        <span className="text-xs font-medium text-text-muted">
          {locationName} Share
        </span>
        <Segmented
          aria-label={`${locationName} share mode`}
          options={MODE_OPTIONS}
          value={share.isPercent ? 'percent' : 'fixed'}
          onChange={(v) => onChange({ isPercent: v === 'percent' })}
        />
      </div>

      {share.isPercent ? (
        <div className="flex items-end gap-4 flex-wrap">
          <InlineNumberField
            label="Cash"
            value={share.cash}
            onChange={(v) => onChange({ cash: v })}
          />
          <InlineNumberField
            label="Insurance"
            value={share.insurance}
            onChange={(v) => onChange({ insurance: v })}
          />
          <InlineNumberField
            label="Corporate"
            value={share.corporate}
            onChange={(v) => onChange({ corporate: v })}
          />
        </div>
      ) : (
        <div className="flex items-end gap-4">
          <InlineNumberField
            label="Amount"
            value={share.fixed}
            onChange={(v) => onChange({ fixed: v })}
            wide
          />
        </div>
      )}
    </div>
  );
}

function InlineNumberField({
  label,
  value,
  onChange,
  wide,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  wide?: boolean;
}) {
  return (
    <TextField
      label={label}
      type="text"
      size="compact"
      inputMode="decimal"
      placeholder="0"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      containerClassName={wide ? 'w-36' : 'w-24'}
      inputClassName="text-right tabular-nums"
      hideSpinButtons
    />
  );
}
